/**
 * Product terminology by tenant app type.
 * Trucking is the primary profile today; serviceRepair entries are stubs for later expansion.
 */

export const APP_TYPES = {
  TRUCKING: "trucking",
  SERVICE_REPAIR: "serviceRepair",
};

const TERMINOLOGY_BY_APP_TYPE = {
  [APP_TYPES.TRUCKING]: {
    job: "Job",
    jobPlural: "Jobs",
    jobLower: "job",
    leg: "Load Leg",
    legPlural: "Load Legs",
    legLower: "load leg",
    customNumberLabel: "Custom Job #",
    loadNumberLabel: "Load Number",
    transactionDateLabel: "Transaction Date",
    loadInformationSection: "Job Information",
    legInformationSection: "Leg Information",
    loadNotesLabel: "Job Notes",
    loadTypeLabel: "Load Type",
    jobTemplateTitle: "Job Templates",
    legTemplateTitle: "Load Leg Templates",
    jobTemplateSubtitle:
      "Reuse job defaults, rates, and optional checklist groups.",
    legTemplateSubtitle:
      "Reuse pickup, delivery, mileage, charge, schedule, and checklist defaults.",
    jobTemplateEmpty:
      "Create a job template to prefill repeatable trucking work.",
    legTemplateEmpty:
      "Create a leg template to speed up common pickup, delivery, or return loads.",
    jobTemplatePlaceholder: "Standard job template",
    legTemplatePlaceholder: "Standard pickup leg",
    originLabel: "Origin",
    destinationLabel: "Destination",
    routeDefaultsSection: "Route Defaults",
    routeDefaultsHint:
      "Optional default pickup and delivery locations for this leg template.",
    jobTemplateSelectLabel: "Job Template",
    createJobTitle: "Create a new job",
    createJobFromFileTitle: "Create job from file",
    jobSummarySection: "Job summary",
    legsSectionTitle: "Load legs",
    dispatchUnassignedTab: "Unassigned legs",
    dispatchAssignedTab: "Assigned legs",
    loadInfoSection: "Job info",
    otherLegsOnLoad: "Other legs on this job",
    tripManifestTitle: "Create trip manifest",
    tripManifestListTitle: "Trip manifests",
    tripManifestCreateAction: "Create trip manifest",
    tripManifestSubtitle:
      "Group this driver's legs into one compliance trip packet. Assigning a driver on dispatch does not replace manifest readiness checks.",
    tripManifestAutoLegsHint:
      "Includes this driver's unmanifested legs with appointments in this date range.",
    tripManifestComplianceHint:
      "Sets default shipping checks for this trip (for example hazmat or reefer). You can update leg details later.",
    assignVsBuildTripHint:
      "Assign driver updates the schedule. Build trip opens a trip manifest with this leg for compliance paperwork (BOL, dispatch snapshot).",
    assignmentScopeHint:
      "Current leg only affects this stop. Include unassigned applies the same driver to sibling legs without a driver.",
    dispatchDraftFromDispatchWithDriver:
      "Pre-filled from dispatch. Review legs and create the trip manifest.",
    dispatchDraftFromDispatchWithoutDriver:
      "Pre-filled from dispatch. Select the driver — unassigned legs are assigned when you create the trip manifest.",
    completeJob: "Complete Job",
    navDispatch: "Dispatch",
    totalJobsLabel: "Total Jobs",
    jobCountLabel: "Job Count",
    noJobsInDateRange: "There are no jobs in this selected date range.",
  },
  [APP_TYPES.SERVICE_REPAIR]: {
    job: "Job",
    jobPlural: "Jobs",
    jobLower: "job",
    leg: "Leg",
    legPlural: "Legs",
    legLower: "leg",
    customNumberLabel: "Custom Job #",
    loadNumberLabel: "Order Number",
    transactionDateLabel: "Appointment Date",
    loadInformationSection: "Job Information",
    legInformationSection: "Leg Information",
    loadNotesLabel: "Job Summary / Original Call",
    loadTypeLabel: "Leg Type",
    jobTemplateTitle: "Job Templates",
    legTemplateTitle: "Leg Templates",
    jobTemplateSubtitle:
      "Reuse job defaults, service items, rates, and optional checklist groups.",
    legTemplateSubtitle:
      "Reuse route, charge, schedule, and checklist defaults for legs.",
    jobTemplateEmpty: "Create a job template to prefill repeatable service work.",
    legTemplateEmpty: "Create a leg template to speed up repeatable work segments.",
    jobTemplatePlaceholder: "Standard install job",
    legTemplatePlaceholder: "Standard service leg",
    originLabel: "Service Location",
    destinationLabel: "Destination",
    routeDefaultsSection: "Route Defaults",
    routeDefaultsHint:
      "Optional default locations for this leg template.",
    jobTemplateSelectLabel: "Job Template",
    createJobTitle: "Create a new job",
    createJobFromFileTitle: "Create job from file",
    jobSummarySection: "Job summary",
    legsSectionTitle: "Legs",
    dispatchUnassignedTab: "Unassigned jobs",
    dispatchAssignedTab: "Assigned legs",
    loadInfoSection: "Job info",
    otherLegsOnLoad: "Other legs in job",
    tripManifestTitle: "Create trip manifest",
    tripManifestListTitle: "Trip manifests",
    tripManifestCreateAction: "Create trip manifest",
    tripManifestSubtitle:
      "Group legs into a trip manifest for operational execution.",
    tripManifestAutoLegsHint:
      "Includes unmanifested legs with appointments in this date range.",
    tripManifestComplianceHint:
      "Sets default checks for this trip. You can update leg details later.",
    assignVsBuildTripHint:
      "Assign driver updates the schedule. Build trip groups legs into a trip manifest.",
    assignmentScopeHint:
      "Current leg only affects this stop. Include unassigned applies the same resource to sibling legs without an assignment.",
    dispatchDraftFromDispatchWithDriver:
      "Pre-filled from dispatch. Review and create the trip manifest.",
    dispatchDraftFromDispatchWithoutDriver:
      "Pre-filled from dispatch. Select a resource before creating the trip manifest.",
    completeJob: "Complete Job",
    navDispatch: "Schedule",
    totalJobsLabel: "Total Jobs",
    jobCountLabel: "Job Count",
    noJobsInDateRange: "There are no jobs in this selected date range.",
  },
};

/**
 * @param {string|null|undefined} appType
 * @returns {typeof TERMINOLOGY_BY_APP_TYPE[typeof APP_TYPES.TRUCKING]}
 */
export function getProductTerminology(appType) {
  if (appType === APP_TYPES.TRUCKING) {
    return TERMINOLOGY_BY_APP_TYPE[APP_TYPES.TRUCKING];
  }

  return TERMINOLOGY_BY_APP_TYPE[APP_TYPES.SERVICE_REPAIR];
}

/**
 * @param {string|null|undefined} appType
 * @returns {boolean}
 */
export function isTruckingAppType(appType) {
  return appType === APP_TYPES.TRUCKING;
}
